import { useMemo, useState } from "react";
import { api } from "../api";
import { day, num, signClass } from "../format";
import type { Trade } from "../types";
import { EditTrade } from "./EditTrade";

/**
 * A dense table of every trade, for reviewing and correcting in bulk.
 *
 * The Trades tab shows one rich card per position, for thinking about a single
 * trade. This is the opposite view — many trades at a glance, every fill
 * listed, so a wrong quantity or price is easy to spot and fix.
 */
export function ManageTrades({
  trades,
  onChanged,
  onDeleted,
}: {
  trades: Trade[];
  onChanged: (t: Trade) => void;
  onDeleted: (id: number) => void;
}) {
  const [editing, setEditing] = useState<Trade | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(
    () => trades.filter((t) => (search ? t.symbol.includes(search.toUpperCase()) : true)),
    [trades, search],
  );

  const totals = useMemo(() => {
    const closed = rows.filter((t) => t.net_pnl !== null);
    return {
      realized: closed.reduce((a, t) => a + (t.net_pnl ?? 0), 0),
      trusted: closed.filter((t) => !t.anomaly).reduce((a, t) => a + (t.net_pnl ?? 0), 0),
      flagged: closed.filter((t) => t.anomaly).length,
    };
  }, [rows]);

  return (
    <>
      <div className="toolbar">
        <div className="field">
          <label>Search by symbol</label>
          <input
            value={search}
            placeholder="Search by symbol…"
            onChange={(e) => setSearch(e.target.value.toUpperCase())}
          />
        </div>
        <div className="spacer" />
        <div className="stat">
          <div className="k">Total P/L (₹)</div>
          <div className={`v ${signClass(totals.realized)}`}>{num(totals.realized)}</div>
        </div>
        <div className="stat">
          <div className="k">Excluding flagged</div>
          <div className={`v ${signClass(totals.trusted)}`}>{num(totals.trusted)}</div>
        </div>
      </div>

      {error && <div className="banner error">{error}</div>}

      {totals.flagged > 0 && (
        <div className="banner warn">
          {totals.flagged} trade{totals.flagged === 1 ? " is" : "s are"} flagged and excluded from
          the second total — usually a position opened before your imported range, or an
          unconfirmed corporate action.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="card empty">
          <h3>No trades</h3>
          <p className="muted">Trades you enter or import will be listed here.</p>
        </div>
      ) : (
        <div className="card" style={{ overflowX: "auto" }}>
          <table className="legs manage">
            <thead>
              <tr>
                <th>Symbol</th>
                <th colSpan={3}>Entry</th>
                <th colSpan={3}>Exit</th>
                <th>P/L % (₹)</th>
                <th />
              </tr>
              <tr className="subhead">
                <th />
                <th>Qty</th>
                <th>Price</th>
                <th>Date / Time</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Date / Time</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((trade) => {
                const buys = trade.legs.filter((l) => l.kind === "buy");
                const sells = trade.legs.filter((l) => l.kind === "sell");
                const lines = Math.max(buys.length, sells.length, 1);

                return (
                  <tr key={trade.id}>
                    <td>
                      <strong>{trade.symbol}</strong>
                      {trade.product === "mtf" && (
                        <div>
                          <span className="badge mtf" title="Margin funded">
                            MTF
                          </span>
                        </div>
                      )}
                      {trade.anomaly && (
                        <div>
                          <span className="badge muted" title={trade.anomaly}>
                            flagged
                          </span>
                        </div>
                      )}
                      {trade.source === "imported" && (
                        <div>
                          <span className="badge muted">imported</span>
                        </div>
                      )}
                    </td>

                    {/* Fills stack inside the cell so one row stays one trade. */}
                    <Fills legs={buys} lines={lines} fallback={trade} kind="buy" />
                    <Fills legs={sells} lines={lines} fallback={trade} kind="sell" />

                    <td className={signClass(trade.net_pnl)}>
                      {trade.net_pnl === null
                        ? "N/A"
                        : `${num(trade.pct_return)} (${num(trade.net_pnl)})`}
                    </td>

                    <td>
                      <div className="row-actions">
                        {trade.source === "manual" ? (
                          <>
                            <button
                              className="ghost"
                              title="Edit trade"
                              onClick={() => setEditing(trade)}
                            >
                              ✎
                            </button>
                            <button
                              className="ghost danger"
                              title="Delete trade"
                              onClick={async () => {
                                if (!confirm(`Delete ${trade.symbol}? This cannot be undone.`)) {
                                  return;
                                }
                                try {
                                  await api.deleteTrade(trade.id);
                                  onDeleted(trade.id);
                                } catch (e) {
                                  setError((e as Error).message);
                                }
                              }}
                            >
                              🗑
                            </button>
                          </>
                        ) : (
                          <span
                            className="hint"
                            title="Built from imported broker fills — re-import to change it."
                          >
                            —
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditTrade trade={editing} onSaved={onChanged} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

function Fills({
  legs,
  lines,
  fallback,
  kind,
}: {
  legs: Trade["legs"];
  lines: number;
  fallback: Trade;
  kind: "buy" | "sell";
}) {
  // Imported trades keep their fills in Execution, not TradeLeg, so fall back to
  // the trade's own averages rather than rendering an empty row.
  if (legs.length === 0) {
    const isBuy = kind === "buy";
    const price = isBuy ? fallback.avg_entry : fallback.avg_exit;
    const date = isBuy ? fallback.entry_date : fallback.exit_date;
    if (price === null || date === null) {
      return (
        <>
          <td className="muted">N/A</td>
          <td className="muted">N/A</td>
          <td className="muted">N/A</td>
        </>
      );
    }
    return (
      <>
        <td>{fallback.qty}</td>
        <td>{num(price)}</td>
        <td className="nowrap">{day(date)}</td>
      </>
    );
  }

  const pad = Array.from({ length: lines - legs.length });
  return (
    <>
      <td>
        {legs.map((l) => (
          <div key={l.id}>{l.quantity}</div>
        ))}
        {pad.map((_, i) => (
          <div key={i}>&nbsp;</div>
        ))}
      </td>
      <td>
        {legs.map((l) => (
          <div key={l.id}>{num(l.price)}</div>
        ))}
        {pad.map((_, i) => (
          <div key={i}>&nbsp;</div>
        ))}
      </td>
      <td className="nowrap">
        {legs.map((l) => (
          <div key={l.id}>
            {day(l.leg_date)}
            {l.leg_time && <span className="muted"> {l.leg_time.slice(0, 5)}</span>}
          </div>
        ))}
        {pad.map((_, i) => (
          <div key={i}>&nbsp;</div>
        ))}
      </td>
    </>
  );
}
